import type { ConstraintLevel } from '@bp/shared-planung-utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type RuleText = { name: string; desc: string };
export type CategoryText = { title: string; desc: string };

/**
 * Verständliche Texte der Seite „Regeln für die Generierung“: Name und Kurzbeschreibung je Regel (Schlüssel: id bei
 * Einzelregeln, `family` bei Stufen-Familien), Titel und Erklärung je Kategorie, Stufen-Namen. Die Schlüssel stehen
 * ausgeschrieben da, damit i18next-parser sie findet. Enthält auch Regeln, die erst mit einem neueren Katalog aus
 * @bp/shared-planung-utils kommen; fehlt ein Text, zeigt die Seite das Katalog-Label.
 */
export const useConstraintCatalogTexts = () => {
  const { t } = useTranslation();
  return useMemo(() => {
    const rules: Record<string, RuleText> = {
      teacher0900: {
        name: t('constraintCatalog.rule.teacher0900.name'),
        desc: t('constraintCatalog.rule.teacher0900.desc'),
      },
      class1900: { name: t('constraintCatalog.rule.class1900.name'), desc: t('constraintCatalog.rule.class1900.desc') },
      room3900: { name: t('constraintCatalog.rule.room3900.name'), desc: t('constraintCatalog.rule.room3900.desc') },
      sys9001: { name: t('constraintCatalog.rule.sys9001.name'), desc: t('constraintCatalog.rule.sys9001.desc') },
      sys9002: { name: t('constraintCatalog.rule.sys9002.name'), desc: t('constraintCatalog.rule.sys9002.desc') },
      sys9003: { name: t('constraintCatalog.rule.sys9003.name'), desc: t('constraintCatalog.rule.sys9003.desc') },
      sys9004: { name: t('constraintCatalog.rule.sys9004.name'), desc: t('constraintCatalog.rule.sys9004.desc') },
      sys9005: { name: t('constraintCatalog.rule.sys9005.name'), desc: t('constraintCatalog.rule.sys9005.desc') },
      room3001: { name: t('constraintCatalog.rule.room3001.name'), desc: t('constraintCatalog.rule.room3001.desc') },
      room3002: { name: t('constraintCatalog.rule.room3002.name'), desc: t('constraintCatalog.rule.room3002.desc') },
      room3003: { name: t('constraintCatalog.rule.room3003.name'), desc: t('constraintCatalog.rule.room3003.desc') },
      class1912: { name: t('constraintCatalog.rule.class1912.name'), desc: t('constraintCatalog.rule.class1912.desc') },
      class1913: { name: t('constraintCatalog.rule.class1913.name'), desc: t('constraintCatalog.rule.class1913.desc') },
      'class1003-nogap': {
        name: t('constraintCatalog.rule.class1003-nogap.name'),
        desc: t('constraintCatalog.rule.class1003-nogap.desc'),
      },
      class1001: { name: t('constraintCatalog.rule.class1001.name'), desc: t('constraintCatalog.rule.class1001.desc') },
      'class1002-min': {
        name: t('constraintCatalog.rule.class1002-min.name'),
        desc: t('constraintCatalog.rule.class1002-min.desc'),
      },
      klasseMaxStunden: {
        name: t('constraintCatalog.rule.klasseMaxStunden.name'),
        desc: t('constraintCatalog.rule.klasseMaxStunden.desc'),
      },
      klasseFreistunden: {
        name: t('constraintCatalog.rule.klasseFreistunden.name'),
        desc: t('constraintCatalog.rule.klasseFreistunden.desc'),
      },
      klasseGleichzeitigEnde: {
        name: t('constraintCatalog.rule.klasseGleichzeitigEnde.name'),
        desc: t('constraintCatalog.rule.klasseGleichzeitigEnde.desc'),
      },
      klasseUnknownPos: {
        name: t('constraintCatalog.rule.klasseUnknownPos.name'),
        desc: t('constraintCatalog.rule.klasseUnknownPos.desc'),
      },
      klasseGebaeudewechselTag: {
        name: t('constraintCatalog.rule.klasseGebaeudewechselTag.name'),
        desc: t('constraintCatalog.rule.klasseGebaeudewechselTag.desc'),
      },
      klasseGebaeudewechselWoche: {
        name: t('constraintCatalog.rule.klasseGebaeudewechselWoche.name'),
        desc: t('constraintCatalog.rule.klasseGebaeudewechselWoche.desc'),
      },
      teacher0901: {
        name: t('constraintCatalog.rule.teacher0901.name'),
        desc: t('constraintCatalog.rule.teacher0901.desc'),
      },
      teacher0902: {
        name: t('constraintCatalog.rule.teacher0902.name'),
        desc: t('constraintCatalog.rule.teacher0902.desc'),
      },
      subject2900: {
        name: t('constraintCatalog.rule.subject2900.name'),
        desc: t('constraintCatalog.rule.subject2900.desc'),
      },
      class1901: { name: t('constraintCatalog.rule.class1901.name'), desc: t('constraintCatalog.rule.class1901.desc') },
      class1902: { name: t('constraintCatalog.rule.class1902.name'), desc: t('constraintCatalog.rule.class1902.desc') },
      randstunde: {
        name: t('constraintCatalog.rule.randstunde.name'),
        desc: t('constraintCatalog.rule.randstunde.desc'),
      },
      'teacher0002-min': {
        name: t('constraintCatalog.rule.teacher0002-min.name'),
        desc: t('constraintCatalog.rule.teacher0002-min.desc'),
      },
      'teacher0001-min': {
        name: t('constraintCatalog.rule.teacher0001-min.name'),
        desc: t('constraintCatalog.rule.teacher0001-min.desc'),
      },
      lehrerMaxStunden: {
        name: t('constraintCatalog.rule.lehrerMaxStunden.name'),
        desc: t('constraintCatalog.rule.lehrerMaxStunden.desc'),
      },
      lehrerMaxTage: {
        name: t('constraintCatalog.rule.lehrerMaxTage.name'),
        desc: t('constraintCatalog.rule.lehrerMaxTage.desc'),
      },
      lehrerUnknownPos: {
        name: t('constraintCatalog.rule.lehrerUnknownPos.name'),
        desc: t('constraintCatalog.rule.lehrerUnknownPos.desc'),
      },
      lehrerMaxBlock: {
        name: t('constraintCatalog.rule.lehrerMaxBlock.name'),
        desc: t('constraintCatalog.rule.lehrerMaxBlock.desc'),
      },
      lehrerFreistunden: {
        name: t('constraintCatalog.rule.lehrerFreistunden.name'),
        desc: t('constraintCatalog.rule.lehrerFreistunden.desc'),
      },
      lehrerGebaeudewechselTag: {
        name: t('constraintCatalog.rule.lehrerGebaeudewechselTag.name'),
        desc: t('constraintCatalog.rule.lehrerGebaeudewechselTag.desc'),
      },
      lehrerGebaeudewechselWoche: {
        name: t('constraintCatalog.rule.lehrerGebaeudewechselWoche.name'),
        desc: t('constraintCatalog.rule.lehrerGebaeudewechselWoche.desc'),
      },
      fachMaxTaeglich: {
        name: t('constraintCatalog.rule.fachMaxTaeglich.name'),
        desc: t('constraintCatalog.rule.fachMaxTaeglich.desc'),
      },
      fachMaxTageWoche: {
        name: t('constraintCatalog.rule.fachMaxTageWoche.name'),
        desc: t('constraintCatalog.rule.fachMaxTageWoche.desc'),
      },
      mittagspause: {
        name: t('constraintCatalog.rule.mittagspause.name'),
        desc: t('constraintCatalog.rule.mittagspause.desc'),
      },
      dep4001: { name: t('constraintCatalog.rule.dep4001.name'), desc: t('constraintCatalog.rule.dep4001.desc') },
      dep4002: { name: t('constraintCatalog.rule.dep4002.name'), desc: t('constraintCatalog.rule.dep4002.desc') },
      dep4003: { name: t('constraintCatalog.rule.dep4003.name'), desc: t('constraintCatalog.rule.dep4003.desc') },
      dep4004: { name: t('constraintCatalog.rule.dep4004.name'), desc: t('constraintCatalog.rule.dep4004.desc') },
      dep4005: { name: t('constraintCatalog.rule.dep4005.name'), desc: t('constraintCatalog.rule.dep4005.desc') },
      dep4006: { name: t('constraintCatalog.rule.dep4006.name'), desc: t('constraintCatalog.rule.dep4006.desc') },
      dep4007: { name: t('constraintCatalog.rule.dep4007.name'), desc: t('constraintCatalog.rule.dep4007.desc') },
      dep4008: { name: t('constraintCatalog.rule.dep4008.name'), desc: t('constraintCatalog.rule.dep4008.desc') },
      dep4009: { name: t('constraintCatalog.rule.dep4009.name'), desc: t('constraintCatalog.rule.dep4009.desc') },
      dep4010: { name: t('constraintCatalog.rule.dep4010.name'), desc: t('constraintCatalog.rule.dep4010.desc') },
      dep4012: { name: t('constraintCatalog.rule.dep4012.name'), desc: t('constraintCatalog.rule.dep4012.desc') },
      class1914: { name: t('constraintCatalog.rule.class1914.name'), desc: t('constraintCatalog.rule.class1914.desc') },
      class1915: { name: t('constraintCatalog.rule.class1915.name'), desc: t('constraintCatalog.rule.class1915.desc') },
      class1916: { name: t('constraintCatalog.rule.class1916.name'), desc: t('constraintCatalog.rule.class1916.desc') },
      class1917: { name: t('constraintCatalog.rule.class1917.name'), desc: t('constraintCatalog.rule.class1917.desc') },
      class1918: { name: t('constraintCatalog.rule.class1918.name'), desc: t('constraintCatalog.rule.class1918.desc') },
      class1919: { name: t('constraintCatalog.rule.class1919.name'), desc: t('constraintCatalog.rule.class1919.desc') },
      class1920: { name: t('constraintCatalog.rule.class1920.name'), desc: t('constraintCatalog.rule.class1920.desc') },
      klasseZeitfenster: {
        name: t('constraintCatalog.rule.klasseZeitfenster.name'),
        desc: t('constraintCatalog.rule.klasseZeitfenster.desc'),
      },
      subject2008: {
        name: t('constraintCatalog.rule.subject2008.name'),
        desc: t('constraintCatalog.rule.subject2008.desc'),
      },
      subject2009: {
        name: t('constraintCatalog.rule.subject2009.name'),
        desc: t('constraintCatalog.rule.subject2009.desc'),
      },
      fachMaxDoppelstunden: {
        name: t('constraintCatalog.rule.fachMaxDoppelstunden.name'),
        desc: t('constraintCatalog.rule.fachMaxDoppelstunden.desc'),
      },
      teacher0008: {
        name: t('constraintCatalog.rule.teacher0008.name'),
        desc: t('constraintCatalog.rule.teacher0008.desc'),
      },
      teacher0009: {
        name: t('constraintCatalog.rule.teacher0009.name'),
        desc: t('constraintCatalog.rule.teacher0009.desc'),
      },
    };
    const categories: Record<string, CategoryText> = {
      CONFLICT: {
        title: t('constraintCatalog.category.CONFLICT.title'),
        desc: t('constraintCatalog.category.CONFLICT.desc'),
      },
      STRUCTURE: {
        title: t('constraintCatalog.category.STRUCTURE.title'),
        desc: t('constraintCatalog.category.STRUCTURE.desc'),
      },
      ROOM: { title: t('constraintCatalog.category.ROOM.title'), desc: t('constraintCatalog.category.ROOM.desc') },
      DIVISION: {
        title: t('constraintCatalog.category.DIVISION.title'),
        desc: t('constraintCatalog.category.DIVISION.desc'),
      },
      CLASS: { title: t('constraintCatalog.category.CLASS.title'), desc: t('constraintCatalog.category.CLASS.desc') },
      DISTRIBUTION: {
        title: t('constraintCatalog.category.DISTRIBUTION.title'),
        desc: t('constraintCatalog.category.DISTRIBUTION.desc'),
      },
      TEACHER: {
        title: t('constraintCatalog.category.TEACHER.title'),
        desc: t('constraintCatalog.category.TEACHER.desc'),
      },
      SUBJECT: {
        title: t('constraintCatalog.category.SUBJECT.title'),
        desc: t('constraintCatalog.category.SUBJECT.desc'),
      },
      LUNCH: { title: t('constraintCatalog.category.LUNCH.title'), desc: t('constraintCatalog.category.LUNCH.desc') },
      DEPENDENCY: {
        title: t('constraintCatalog.category.DEPENDENCY.title'),
        desc: t('constraintCatalog.category.DEPENDENCY.desc'),
      },
    };
    const levels: Record<ConstraintLevel, string> = {
      HARD: t('constraintCatalog.level.HARD'),
      MEDIUM: t('constraintCatalog.level.MEDIUM'),
      SOFT: t('constraintCatalog.level.SOFT'),
    };
    return { rules, categories, levels };
  }, [t]);
};
